// Shared geometries, materials, baked sub-meshes, and scatter-config types for
// TerrainScatter. Module-level singletons created once at load — safe because
// JS is single-threaded and the instanced-mesh effects only read them.
//
// Props use MeshBasicMaterial (#122, audit 2026-07-16: PBR shading crushed them
// to black at the dim day-night ambient floor), so there is no lighting. Depth
// is faked instead: every foliage/bark geometry carries baked vertex colours —
// a vertical gradient (darker underside, lighter crown) times a per-face tone
// jitter that reads as leaf/bark texture. The per-instance colour (see
// TerrainScatter's palettes) multiplies on top, so one geometry serves every
// tint.

import * as THREE from 'three';
import {
  birchFoliageGeo,
  birchTrunkGeo,
  bushGeo,
  cactusArmLBakedGeo,
  cactusArmRBakedGeo,
  cactusGeo,
  fernGeo,
  flowerBlossomGeo,
  flowerStemGeo,
  grassABakedGeo,
  grassBBakedGeo,
  grassCBakedGeo,
  jungleFoliageGeo,
  jungleTrunkGeo,
  oakFoliageGeo,
  oakTrunkGeo,
  palmFoliageGeo,
  palmTrunkGeo,
  pineFoliageGeo,
  pineTrunkGeo,
  rockGeo,
  snowBoulderGeo,
  spruceFoliageGeo,
  spruceTrunkGeo,
} from './scatterGeometries.js';

// The geometries and the bake helpers live in scatterGeometries.ts; the names
// other modules import from here are re-exported.
export {
  bakeShade,
  cactusArmLBakedGeo,
  cactusArmRBakedGeo,
  grassABakedGeo,
  grassBBakedGeo,
  grassCBakedGeo,
  merge,
} from './scatterGeometries.js';

// --- Materials ---
// White base + vertexColors: the baked shade and the per-instance colour
// (instanceColor) supply all of the tint.

const shadedMat = (): THREE.MeshBasicMaterial =>
  new THREE.MeshBasicMaterial({ color: 'rgb(255,255,255)', vertexColors: true });

// --- Wind sway ---
// Shared time uniform, advanced by WindAnimator (TerrainScatter.tsx). Sway is
// a vertex-shader bend that grows with local height squared, so blades pivot
// at the ground and foliage crowns lean while their bases stay planted. The
// phase comes from the instance origin's world XZ, so neighbours drift out of
// step instead of pulsing in sync. Trunks (barkMat) and rocks stay rigid.
export const windTime = { value: 0 };

function applyWindSway(
  mat: THREE.MeshBasicMaterial,
  { amp, minY, spanY }: { amp: number; minY: number; spanY: number },
): void {
  const f = (v: number) => v.toFixed(5);
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uWindTime = windTime;
    shader.vertexShader = `uniform float uWindTime;\n${shader.vertexShader}`.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
      #ifdef USE_INSTANCING
      {
        float _ph = instanceMatrix[3][0] * 1.9 + instanceMatrix[3][2] * 1.4;
        float _h = clamp((transformed.y - ${f(minY)}) / ${f(spanY)}, 0.0, 1.0);
        float _sway = (sin(uWindTime * 1.8 + _ph) + 0.45 * sin(uWindTime * 3.1 + _ph * 1.7 + 1.3))
          * ${f(amp)} * _h * _h;
        transformed.x += _sway;
        transformed.z += 0.7 * _sway;
      }
      #endif`,
    );
  };
  // Unique per config: three's default program cache key is the *source* of
  // onBeforeCompile, and the closure constants don't show up in it — without
  // this, grass and foliage would share whichever program compiled first.
  mat.customProgramCacheKey = () => `scatter-wind:${amp}:${minY}:${spanY}`;
}

export const foliageMat = shadedMat();
export const barkMat = shadedMat();
export const rockMat = shadedMat();
// Grass blades (local y 0..0.18) flutter at the tip; foliage crowns
// (~y 0.25..0.9) breathe. Kept subtle — the board must stay readable.
applyWindSway(foliageMat, { amp: 0.02, minY: 0.25, spanY: 0.65 });
export const grassMat = new THREE.MeshBasicMaterial({
  color: 'rgb(255,255,255)',
  side: THREE.DoubleSide,
});
applyWindSway(grassMat, { amp: 0.026, minY: 0.0, spanY: 0.18 });
// Blossoms flutter a touch less than grass blades (they sit higher, so the
// same amplitude would read bigger).
export const flowerMat = shadedMat();
applyWindSway(flowerMat, { amp: 0.02, minY: 0.05, spanY: 0.2 });
export const cactusMat = new THREE.MeshBasicMaterial({ color: 'rgb(255,255,255)' });
export const snowBoulderMat = new THREE.MeshBasicMaterial({ color: 'rgb(245,250,255)' });

export type TreeSpecies =
  | 'pine'
  | 'spruce'
  | 'oak'
  | 'birch'
  | 'bush'
  | 'palm'
  | 'jungleTree'
  | 'fern';
export type ScatterPropType = TreeSpecies | 'rock' | 'grass' | 'flower' | 'cactus' | 'snowBoulder';

/** Which palette tints a sub-mesh: the prop's main colour, or its trunk. */
export type SubMeshRole = 'main' | 'trunk';

export interface ScatterSubMesh {
  readonly geometry: THREE.BufferGeometry;
  readonly material: THREE.Material;
  readonly role: SubMeshRole;
}

export const PROP_SUBMESHES: Record<ScatterPropType, readonly ScatterSubMesh[]> = {
  pine: [
    { geometry: pineTrunkGeo, material: barkMat, role: 'trunk' },
    { geometry: pineFoliageGeo, material: foliageMat, role: 'main' },
  ],
  spruce: [
    { geometry: spruceTrunkGeo, material: barkMat, role: 'trunk' },
    { geometry: spruceFoliageGeo, material: foliageMat, role: 'main' },
  ],
  oak: [
    { geometry: oakTrunkGeo, material: barkMat, role: 'trunk' },
    { geometry: oakFoliageGeo, material: foliageMat, role: 'main' },
  ],
  birch: [
    { geometry: birchTrunkGeo, material: barkMat, role: 'trunk' },
    { geometry: birchFoliageGeo, material: foliageMat, role: 'main' },
  ],
  bush: [{ geometry: bushGeo, material: foliageMat, role: 'main' }],
  palm: [
    { geometry: palmTrunkGeo, material: barkMat, role: 'trunk' },
    { geometry: palmFoliageGeo, material: foliageMat, role: 'main' },
  ],
  jungleTree: [
    { geometry: jungleTrunkGeo, material: barkMat, role: 'trunk' },
    { geometry: jungleFoliageGeo, material: foliageMat, role: 'main' },
  ],
  fern: [{ geometry: fernGeo, material: foliageMat, role: 'main' }],
  rock: [{ geometry: rockGeo, material: rockMat, role: 'main' }],
  grass: [
    { geometry: grassABakedGeo, material: grassMat, role: 'main' },
    { geometry: grassBBakedGeo, material: grassMat, role: 'main' },
    { geometry: grassCBakedGeo, material: grassMat, role: 'main' },
  ],
  flower: [
    { geometry: flowerStemGeo, material: barkMat, role: 'trunk' },
    { geometry: flowerBlossomGeo, material: flowerMat, role: 'main' },
  ],
  cactus: [
    { geometry: cactusGeo, material: cactusMat, role: 'main' },
    { geometry: cactusArmLBakedGeo, material: cactusMat, role: 'main' },
    { geometry: cactusArmRBakedGeo, material: cactusMat, role: 'main' },
  ],
  snowBoulder: [{ geometry: snowBoulderGeo, material: snowBoulderMat, role: 'main' }],
};

/** A weighted species pick for a scatter config (e.g. forest = pine/spruce/oak/birch/bush). */
export interface WeightedProp {
  readonly propType: ScatterPropType;
  readonly weight: number;
}

export interface ScatterConfig {
  /** Stable id: salts the per-tile hashes so configs sharing a biome don't overlap. */
  id: string;
  biomes: string[];
  /** One entry = a single prop type; several = a seeded weighted mix per instance. */
  props: readonly WeightedProp[];
  minCount: number;
  maxCount: number;
  castShadow: boolean;
  yOffset: number;
  /** Instance size multiplier (default 1) — boulders up-size the same rock geometry. */
  scale?: number;
  /**
   * Cluster instances into per-tile patches (grass tufts) instead of
   * scattering them uniformly across the tile.
   */
  patchy?: boolean;
  /**
   * Pick the instance colour per instance (hash) instead of walking the
   * shared low-frequency field — wildflowers mix hues within a patch,
   * while grove-coherent props (trees) drift together.
   */
  perInstanceColor?: boolean;
  /** Skip entirely during winter (seasonDay active) — e.g. flowers. */
  hideInWinter?: boolean;
  /** Only drawn on maps of these climates (absent = every climate). */
  climates?: readonly string[];
  /** Not drawn on maps of these climates (the jungle replaces the temperate forest). */
  exceptClimates?: readonly string[];
}
